import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Upload, X } from 'lucide-react';
import { DocumentCategory } from '../../lib/api';
import { UploadFormData } from './types';
import { VisibilityField } from './VisibilityField';

interface UploadDialogProps {
  open: boolean;
  uploading: boolean;
  categories: DocumentCategory[];
  uploadForm: UploadFormData;
  onClose: () => void;
  onFormChange: (form: UploadFormData) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function UploadDialog({
  open,
  uploading,
  categories,
  uploadForm,
  onClose,
  onFormChange,
  onFileChange,
  onSubmit,
}: UploadDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a new document or add an external URL link.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={uploadForm.title}
              onChange={(e) => onFormChange({ ...uploadForm, title: e.target.value })}
              placeholder="Enter document title"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={uploadForm.description}
              onChange={(e) => onFormChange({ ...uploadForm, description: e.target.value })}
              placeholder="Enter document description (optional)"
              rows={3}
            />
          </div>

          {/* Folder (CR-5326: was "Category" — renamed for consistency) */}
          <div className="space-y-2">
            <Label htmlFor="category">Folder</Label>
            <Select
              value={uploadForm.category}
              onValueChange={(value) => onFormChange({ ...uploadForm, category: value })}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Select a folder (optional)" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label>Document Type</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="document_type"
                  value="file"
                  checked={uploadForm.document_type === 'file'}
                  onChange={() => onFormChange({ ...uploadForm, document_type: 'file' })}
                  className="cursor-pointer"
                />
                <span>Upload File</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="document_type"
                  value="url"
                  checked={uploadForm.document_type === 'url'}
                  onChange={() => onFormChange({ ...uploadForm, document_type: 'url' })}
                  className="cursor-pointer"
                />
                <span>External URL</span>
              </label>
            </div>
          </div>

          {/* File Upload or URL Input */}
          {uploadForm.document_type === 'file' ? (
            <div className="space-y-2">
              <Label htmlFor="file">
                File <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif,.svg,.bmp"
                  onChange={onFileChange}
                  className="cursor-pointer"
                />
                {uploadForm.file && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onFormChange({ ...uploadForm, file: null })}
                    title="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {uploadForm.file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Allowed file types: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, ZIP, RAR, 7Z, JPG, JPEG, PNG, GIF, SVG, BMP (max 25MB)
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="external_url">
                External URL <span className="text-red-500">*</span>
              </Label>
              <Input
                id="external_url"
                type="url"
                value={uploadForm.external_url}
                onChange={(e) => onFormChange({ ...uploadForm, external_url: e.target.value })}
                placeholder="https://example.com/document.pdf"
                required={uploadForm.document_type === 'url'}
              />
            </div>
          )}

          {/* Additional Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={uploadForm.tags}
                onChange={(e) => onFormChange({ ...uploadForm, tags: e.target.value })}
                placeholder="e.g., contract, invoice"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={uploadForm.version}
                onChange={(e) => onFormChange({ ...uploadForm, version: e.target.value })}
                placeholder="e.g., 1.0, v2.1"
              />
            </div>
          </div>

          {/* Visibility (CR-5325: extracted to a shared VisibilityField so
              the Edit dialog reuses the same UX) */}
          <VisibilityField
            visibility={uploadForm.visibility}
            onVisibilityChange={(v) => onFormChange({ ...uploadForm, visibility: v })}
            sharedCompanyIds={uploadForm.shared_with_company_ids}
            onSharedCompanyIdsChange={(ids) => onFormChange({ ...uploadForm, shared_with_company_ids: ids })}
          />

          {/* CR-5329: Read & Accept toggle. When enabled, recipients are
              required to explicitly accept the document; for shared
              visibility the backend fires a notification to each user. */}
          <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={uploadForm.requires_acceptance}
              onChange={(e) => onFormChange({ ...uploadForm, requires_acceptance: e.target.checked })}
              className="mt-0.5 cursor-pointer"
            />
            <div>
              <p className="text-sm font-medium">Require Read & Accept Receipt</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Recipients must open the document and confirm acceptance. Selected suppliers (Shared visibility) get notified automatically.
              </p>
            </div>
          </label>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

